import React, { useState, useEffect } from 'react'
import { useModalProvider } from '../../../../Providers/ModalProvider'

import { humanReadableSize } from '../../../../Computation/utilFunctions'
import ModernMUITable from "../../../../Components/ModernMUITable"
import styled from 'styled-components'
import { MdClose, MdInfo, MdSearch } from "react-icons/md"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../../../Components/MobergButton/MobergButton"
import { MobergTheme } from "../../../../Components/MobergThemes/MobergColors"
import { MobergFontFamily, MobergFontSize } from "../../../../Components/MobergFont/MobergFont"
import { MobergIconSize } from "../../../../Components/MobergIcon/MobergIcon"
import { PreloadStatusPill } from "../../../../Components/StatusPill/PreloadStatusPill"
import ConfirmationModal from "../../../../Components/ConfirmationModal/ConfirmationModal"
import { parsePrefetchError } from "../DataReview/prefetch/errors"
import { usePrefetchSnapshot, useUnloadFiles } from "../DataReview/prefetch/hooks"
import { derivePreloadStatus, getStorageSummary, getTableStatusLabel } from "../DataReview/prefetch/selectors"

const UnloadFilesModal = () => {
    const { snapshot, isLoading: isSnapshotLoading } = usePrefetchSnapshot()
    const unloadFilesMutation = useUnloadFiles()
    const [selectedFileIds, setSelectedFileIds] = useState([])
    const [searchValue, setSearchValue] = useState("")
    const { close, createModal } = useModalProvider()
    const files = snapshot.files
    // Files the worker is still cleaning up. Purely informational: they are
    // not selectable, and they never block unloading other rows.
    const unloadingCount = files.filter(
        (file) => file.state === "cancelled" || file.state === "unloading"
    ).length

    useEffect(() => {
        if (files.length === 0) {
            setSelectedFileIds([])
        }
    }, [files.length])

    function handleUnload() {
        const dataObjectIds = selectedFileIds.map((dataObjectId) => String(dataObjectId))
        unloadFilesMutation.mutate(dataObjectIds, {
            onSuccess: () => {
                setSelectedFileIds([])
            },
            onError: (error) => {
                console.error("Unable to unload selected files.", error)
                createModal(
                    <ConfirmationModal
                        title="There was a problem"
                        description={parsePrefetchError(error)}
                        confirmButton={{ text: "OK", theme: MobergTheme.BLUE }}
                        showCancelButton={false}
                    />
                )
            },
        })
    }

    const { currentSizeBytes, maxSizeBytes } = getStorageSummary(snapshot)
    const currentSize = humanReadableSize(currentSizeBytes)
    const maxTotalSize = humanReadableSize(maxSizeBytes)
    const clearSearchSelection = (event) => {
        setSearchValue(event.target.value)
        setSelectedFileIds([])
    }

    // Handle row selection change for ModernMUITable
    const handleSelectionChange = (selectionArray) => {
        const selectedIdSet = new Set((selectionArray || []).map((selectionId) => String(selectionId)))
        const selectedRows = files.filter((row) => selectedIdSet.has(String(row.data_object_id)))
        setSelectedFileIds(selectedRows.map((row) => String(row.data_object_id)))
    }

    const getFileName = (row) => {
        const displayedName = String(row.name || "").split("/")
        return displayedName[displayedName.length - 1]
    }

    const columns = [
        { field: 'patient_id', headerName: 'Patient', flex: 0.9, minWidth: 180, sortable: true, visible: true },
        {
            field: 'file_name',
            headerName: 'File name',
            flex: 1.45,
            minWidth: 280,
            sortable: false,
            visible: true
        },
        {
            field: 'obj_type',
            headerName: 'Object type',
            flex: 0.45,
            minWidth: 125,
            sortable: true,
            visible: true,
            renderCell: (params) => params.value || '—'
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.65,
            minWidth: 140,
            sortable: true,
            visible: true,
            renderCell: (params) => (
                <PreloadStatusPill status={{ kind: params.row.statusKind, label: params.value }} />
            )
        },
        {
            field: 'size',
            headerName: 'File size',
            flex: 0.55,
            minWidth: 130,
            sortable: true,
            visible: true,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => humanReadableSize(params.value)
        },
    ]

    // Ensure data has proper IDs for row selection
    const tableData = files.map((row) => ({
        ...row,
        id: String(row.data_object_id),
        file_name: getFileName(row),
        status: getTableStatusLabel(row),
        statusKind: derivePreloadStatus(row).kind,
        size: Number(row.size || 0)
    }))
    const normalizedSearchValue = searchValue.trim().toLowerCase()
    const filteredTableData = normalizedSearchValue
        ? tableData.filter((row) => (
            String(row.patient_id || "").toLowerCase().includes(normalizedSearchValue) ||
            String(row.file_name || "").toLowerCase().includes(normalizedSearchValue) ||
            String(row.obj_type || "").toLowerCase().includes(normalizedSearchValue) ||
            String(row.status || "").toLowerCase().includes(normalizedSearchValue) ||
            humanReadableSize(row.size).toLowerCase().includes(normalizedSearchValue)
        ))
        : tableData

    return (
        <Container>
            <Header>
                <Title>Data ready for review</Title>
                <MobergButton
                    theme={MobergTheme.BLACK}
                    variant={MobergButtonVariant.CLEAR}
                    shape={MobergButtonShape.SQUARE}
                    onClick={close}
                >
                    <MdClose size={MobergIconSize.LARGE} />
                </MobergButton>
            </Header>

            <InfoBand>
                <MdInfo size={MobergIconSize.LARGE} color="var(--color-accent-interactive)" />
                <span>
                    {isSnapshotLoading
                        ? "Loading hot storage status..."
                        : <><strong>{currentSize}</strong> of <strong>{maxTotalSize}</strong> used in hot storage</>}
                </span>
            </InfoBand>

            <TableToolbar>
                <FileCount>
                    {isSnapshotLoading
                        ? "Loading files..."
                        : `${filteredTableData.length} ${filteredTableData.length === 1 ? "file" : "files"}`}
                    {!isSnapshotLoading && unloadingCount > 0 && (
                        <FileCountDetail>· {unloadingCount} unloading…</FileCountDetail>
                    )}
                </FileCount>
                <SearchInputWrapper>
                    <MdSearch size={MobergIconSize.LARGE} />
                    <SearchInput
                        value={searchValue}
                        onChange={clearSearchSelection}
                        placeholder="Search files"
                        disabled={isSnapshotLoading}
                    />
                </SearchInputWrapper>
            </TableToolbar>

            <TableWrapper>
                <ModernMUITable
                    columns={columns}
                    tableData={filteredTableData}
                    loading={isSnapshotLoading}
                    showToolbar={false}
                    enableRowSelection={true}
                    checkboxSelection={false}
                    onSelectionChange={handleSelectionChange}
                    isRowSelectable={(params) => params.row.state !== "cancelled" && params.row.state !== "unloading"}
                    height="100%"
                    paginationMode="client"
                    sortingMode="client"
                    pageSizeOptions={[50, 100]}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 50 }
                        }
                    }}
                    overrideStyles={{
                        border: "1px solid var(--color-border-default)",
                        borderRadius: "4px",
                        "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell": {
                            backgroundColor: "var(--color-hover-active-neutral) !important",
                        },
                        "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
                            backgroundColor: "var(--color-hover-neutral) !important",
                        },
                    }}
                />
            </TableWrapper>

            <ButtonsWrapper>
                <MobergButton
                    onClick={handleUnload}
                    disabled={(selectedFileIds.length === 0) || unloadFilesMutation.isLoading || isSnapshotLoading}
                    theme={MobergTheme.RED}
                    variant={MobergButtonVariant.OUTLINE}
                    shape={MobergButtonShape.WIDE}
                >
                    Unload
                </MobergButton>
            </ButtonsWrapper>
        </Container>
    )
}

export default UnloadFilesModal

const Container = styled.div`
    background: var(--color-surface-panel);
    color: var(--color-text-primary);
    width: min(1100px, calc(100vw - 64px));
    display: flex;
    padding: 28px 36px 32px;
    overflow-x: hidden;
    flex-direction: column;
`

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid var(--color-border-default);
    margin: -28px -36px 0;
    padding: 14px 36px 12px;
`

const Title = styled.h2`
    color: var(--color-text-primary);
    font-family: ${MobergFontFamily.HEADER};
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
    margin: 0;
`

const InfoBand = styled.div`
    align-items: center;
    margin: 16px 0;
    background: var(--color-hover-neutral);
    border-radius: 4px;
    color: var(--color-text-primary);
    display: flex;
    gap: 14px;
    min-height: 52px;
    padding: 12px 18px;

    strong {
        font-weight: bold;
    }
`

const TableToolbar = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 16px;
`

const FileCount = styled.div`
    color: var(--color-text-primary);
    font-family: ${MobergFontFamily.HEADER};
    font-size: ${MobergFontSize.LARGE};
    font-weight: 700;
    display: flex;
    align-items: baseline;
    gap: 8px;
`

const FileCountDetail = styled.span`
    color: var(--text-secondary-color);
    font-family: ${MobergFontFamily.REGULAR};
    font-size: ${MobergFontSize.REGULAR};
    font-weight: 400;
`

const SearchInputWrapper = styled.label`
    align-items: center;
    background: var(--color-input-bg);
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    color: var(--text-secondary-color);
    display: flex;
    gap: 10px;
    min-width: 320px;
    padding: 8px 12px;
`

const SearchInput = styled.input`
    background: transparent;
    border: 0;
    color: var(--color-text-primary);
    font-family: ${MobergFontFamily.REGULAR};
    font-size: ${MobergFontSize.REGULAR};
    min-width: 0;
    outline: 0;
    width: 100%;

    &::placeholder {
        color: var(--text-secondary-color);
    }
`

const TableWrapper = styled.div`
    height: 412px;
    overflow: hidden;
`

const ButtonsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
`
